import type { CoachingClip, CoachingNote } from "@/lib/types";
import { TimecodeChip } from "@/components/ui/TimecodeChip";
import { formatPlayhead } from "@/features/playback/useVideoPlayhead";
import { formatDuration } from "../clipTrack";
import { VideoNoteThumbnail } from "./VideoNoteThumbnail";
import { VideoClipThumbnail } from "./VideoClipThumbnail";

// ════════════════════════════════════════════════════════════════════════
// NotesAndClipsRail — Coach Review v2.
// ────────────────────────────────────────────────────────────────────────
// "Notes on this match" + "Clips on this match", rendered as two
// full-width HORIZONTAL card rails (film strips) below the canvas —
// not in the right rail. Each rail scrolls sideways, so a long match
// with many notes/clips stays compact vertically and never pushes
// the page into dead space.
//
// Why it moved out of the right rail: the rail had to be height-
// locked to the canvas, which squeezed the lists into a tiny nested
// scroll. A full-width horizontal strip gives the lists real room
// and keeps the right rail to tools only.
//
// Clicking a note card jumps the playhead to its `timestamp_seconds`
// (parent's `onJumpToNote`); a clip card opens it for editing
// (parent navigates to `?clip_id=`). Reuses the existing <Button> /
// <TimecodeChip> primitives — no new tokens.
// ════════════════════════════════════════════════════════════════════════

export type NotesAndClipsRailProps = {
  /** Video notes for the active match (already filtered + ordered). */
  notes: CoachingNote[];
  /** Clips for the active match. */
  clips: CoachingClip[];
  /** The note last seeked to — gets the active highlight. */
  activeNoteId: number | null;
  /** Click a note card → seek the playhead to its moment. */
  onJumpToNote: (note: CoachingNote) => void;
  /** Click a clip card → open it (parent navigates to `?clip_id=`). */
  onOpenClip: (clip: CoachingClip) => void;
  /** True while the notes query is still loading. */
  notesLoading?: boolean;
  /** True while the clips query is still loading. */
  clipsLoading?: boolean;
};

/** Left-border accent color for a note card, keyed off its tone. */
function noteAccentClass(noteType: string): string {
  if (noteType === "positive") return "border-l-good";
  if (noteType === "correction") return "border-l-flag";
  return "border-l-rule-strong";
}

/** Section header — mono label + count chip + optional action. */
function SectionHeader(props: {
  label: string;
  count: number;
  action?: React.ReactNode;
}) {
  return (
    <div className="mb-2 flex items-center gap-2">
      <h4 className="font-mono text-[10px] uppercase tracking-mono text-ink-muted">
        {props.label}
      </h4>
      <span className="rounded-full bg-field px-1.5 py-px font-mono text-[10px] text-ink-secondary">
        {props.count}
      </span>
      {props.action ? <div className="ml-auto">{props.action}</div> : null}
    </div>
  );
}

/** A horizontal scrolling strip. Cards are fixed-width so the row
 * reads as a film strip; the strip scrolls sideways past the
 * viewport edge. */
function Strip(props: { children: React.ReactNode; testid: string }) {
  return (
    <div
      className="flex gap-2 overflow-x-auto pb-1"
      data-testid={props.testid}
    >
      {props.children}
    </div>
  );
}

const EMPTY_CARD =
  "flex h-[4.5rem] w-full items-center rounded border border-dashed " +
  "border-rule px-3 font-mono text-xs text-ink-muted";

export function NotesAndClipsRail({
  notes,
  clips,
  activeNoteId,
  onJumpToNote,
  onOpenClip,
  notesLoading,
  clipsLoading,
}: NotesAndClipsRailProps) {
  return (
    <div
      className="flex flex-col gap-4 rounded border border-rule bg-surface p-4"
      data-testid="coach-review-notes-clips-rail"
    >
      {/* ── Notes ──────────────────────────────────────────────── */}
      <section>
        <SectionHeader
          label="Notes on this match"
          count={notes.length}
        />
        {notesLoading ? (
          <p className={EMPTY_CARD}>Loading notes…</p>
        ) : notes.length === 0 ? (
          <p className={EMPTY_CARD} data-testid="coach-review-rail-notes-empty">
            No notes yet — mark a moment to add one.
          </p>
        ) : (
          <Strip testid="coach-review-notes-strip">
            {notes.map((n) => {
              const active = activeNoteId === n.id;
              return (
                <button
                  key={n.id}
                  type="button"
                  data-testid="coach-review-rail-note"
                  data-coach-note-id={n.id}
                  aria-pressed={active}
                  onClick={() => onJumpToNote(n)}
                  className={[
                    "flex w-64 flex-shrink-0 gap-2 overflow-hidden rounded border-l-[3px] px-2.5 py-2 text-left transition-colors",
                    noteAccentClass(n.note_type),
                    active ? "bg-field" : "bg-elevated hover:bg-field",
                  ].join(" ")}
                >
                  <VideoNoteThumbnail
                    note={n}
                    testId="coach-review-rail-note-thumb"
                    placeholderTestId="coach-review-rail-note-thumb-placeholder"
                    overlayTestId="coach-review-rail-note-thumb-overlay"
                    className="relative block h-14 w-24 flex-shrink-0 overflow-hidden rounded border border-rule bg-canvas"
                  />
                  <span className="min-w-0 flex-1">
                    <span className="line-clamp-2 block font-display text-sm font-semibold text-ink">
                      {n.title}
                    </span>
                    <span className="mt-1 flex min-w-0 flex-wrap items-center gap-1.5">
                      <TimecodeChip>{formatPlayhead(n.timestamp_seconds)}</TimecodeChip>
                      <span className="line-clamp-1 font-mono text-[10px] uppercase tracking-mono text-ink-muted">
                        {n.category || n.note_type}
                      </span>
                    </span>
                  </span>
                </button>
              );
            })}
          </Strip>
        )}
      </section>

      {/* ── Clips ──────────────────────────────────────────────── */}
      <section>
        <SectionHeader label="Clips on this match" count={clips.length} />
        {clipsLoading ? (
          <p className={EMPTY_CARD}>Loading clips…</p>
        ) : clips.length === 0 ? (
          <p className={EMPTY_CARD} data-testid="coach-review-rail-clips-empty">
            No clips yet — make one from the clip track.
          </p>
        ) : (
          <Strip testid="coach-review-clips-strip">
            {clips.map((c) => (
              <button
                key={c.id}
                type="button"
                data-testid="coach-review-rail-clip"
                data-coach-clip-id={c.id}
                onClick={() => onOpenClip(c)}
                className="flex w-64 flex-shrink-0 gap-2 overflow-hidden rounded border-l-[3px] border-l-accent bg-elevated px-2.5 py-2 text-left transition-colors hover:bg-field"
              >
                <VideoClipThumbnail
                  clip={c}
                  testId="coach-review-rail-clip-thumb"
                  placeholderTestId="coach-review-rail-clip-thumb-placeholder"
                  overlayTestId="coach-review-rail-clip-thumb-overlay"
                  className="relative block h-14 w-24 flex-shrink-0 overflow-hidden rounded border border-rule bg-canvas"
                />
                <span className="min-w-0 flex-1">
                  <span className="line-clamp-2 block font-display text-sm font-semibold text-ink">
                    {c.title}
                  </span>
                  <span className="mt-1 flex min-w-0 flex-wrap items-center gap-1.5">
                    <TimecodeChip>
                      {formatPlayhead(c.start_seconds)} →{" "}
                      {formatPlayhead(c.end_seconds)}
                    </TimecodeChip>
                    <span
                      data-testid="coach-review-rail-clip-duration"
                      className="shrink-0 font-mono text-[10px] uppercase tracking-mono text-ink-muted"
                    >
                      {formatDuration(c.duration_seconds)}
                    </span>
                  </span>
                </span>
              </button>
            ))}
          </Strip>
        )}
      </section>
    </div>
  );
}
